import { useEffect } from 'react';
import { units } from '@ui/css';
import { applyCssDeclarations } from '@utils/css';
import { tileSelectors } from '../../atom.tile/_tile-input.style.js';

/**
 * As dropzones may be smaller than the tiles that can be placed within, then
 * we must implement support for downscaling the content within tiles such that
 * it perfectly fits the dropzone.
 *
 * NB: Tiles should perfectly fill the dropzone that contains them.
 */
function useDownscaledTileContent(dropzoneRef, tileRef) {
  useEffect(() => {
    const dropzone = dropzoneRef.current;
    const tileWrapper = tileRef.current;
    const tile = tileWrapper?.querySelector(tileSelectors.tile());
    const tileContent = tileWrapper?.querySelector(tileSelectors.content());
    if (!dropzone || !tileWrapper || !tile || !tileContent) {
      return;
    }
    const tileType = tile.dataset["type"];
    if (tileType === "image") {
      return;
    }

    //
    // NB: For singular words we wish to keep them on a single line, if
    // possible, while for all other tiles we only wish to downscale the minimum
    // required amount, such that the content fits within the tile.
    //
    const isSingleWord = tileContent.textContent?.split(" ").length === 1;
    const desiredPadding = tileType === "audio" ? 4 : 10;
    const observer = new ResizeObserver(() => {
      applyCssDeclarations(units.px.__override("0.1rem"), dropzone);
      let currentDownscale = 1.05;
      let bestDownScale = currentDownscale;
      let currentHeight = tileContent.clientHeight;
      while (currentDownscale >= 0.1) {
        applyCssDeclarations(units.px.__override(`${currentDownscale * 0.1}rem`), dropzone);
        const tileHeight = Math.max(desiredPadding, tileWrapper.clientHeight - desiredPadding);
        if (tileContent.clientHeight - tileHeight <= 0) {
          //
          // STEP: Once we reached a point wherein there's no overflow, then
          // we've reached the ideal downscale factor
          //
          if (tileType !== "text" || !isSingleWord) {
            bestDownScale = currentDownscale;
            break;
          }

          //
          // STEP: For single-line words specifically we wish to downscale such
          // that the word fits on a single line, without breaking, if possible.
          //
          if (currentHeight - 12 > tileContent.clientHeight) {
            bestDownScale = currentDownscale;
          }
          currentHeight = tileContent.clientHeight;
        }
        currentDownscale -= 0.1;
      }
      applyCssDeclarations(units.px.__override(`${(bestDownScale - 0.05) * 0.1}rem`), dropzone);
    });
    observer.observe(dropzone);
    return () => {
      observer.disconnect();
    };
  }, [dropzoneRef, tileRef]);
}

export { useDownscaledTileContent };
//# sourceMappingURL=hook.downscale-tile.js.map
